import { useEffect, useRef, useState } from 'react';
import { Login } from '../../apis/api-client';
import { usersClient } from '../../apis/client-instances';
import { Autocomplete, Chip, CircularProgress, TextField } from '../ui';

const SEARCH_PAGE_SIZE = 25;
const DEBOUNCE_MS = 250;
const DEFAULT_MIN_WIDTH = 260;

export interface UserAutocompleteProps {
  /** Single-select value, or array when `multiple` is true. */
  value: Login | Login[] | null;
  onChange: (value: Login | Login[] | null) => void;
  multiple?: boolean;
  /** Restrict results to a specific workspace. */
  workspaceId?: number;
  /** Hide these user IDs from results (e.g. existing group members). */
  excludeIds?: number[];
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  sx?: any;
}

function displayName(user: Login): string {
  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return name || user.email || 'Unknown User';
}

/**
 * Lazy-loaded user picker. Server-side search, debounced typing, optional
 * workspace scoping and exclude-by-id. Supports single- or multi-select.
 *
 * The caller is responsible for hydrating any initial selection (e.g.
 * resolving stored IDs into Login objects) — see usersClient.searchUsersPaged
 * with the `ids` param for bulk resolution.
 */
export default function UserAutocomplete({
  value,
  onChange,
  multiple = false,
  workspaceId,
  excludeIds,
  label,
  placeholder,
  size = 'small',
  fullWidth = false,
  disabled = false,
  required = false,
  error = false,
  helperText,
  sx,
}: UserAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Login[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);

  const reqIdRef = useRef(0);

  // Serialize excludeIds so the effect doesn't re-fire on identity-only changes.
  const excludeKey = (excludeIds ?? []).slice().sort((a, b) => a - b).join(',');

  useEffect(() => {
    if (!open) return;
    const id = ++reqIdRef.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const result = await usersClient.searchUsersPaged(
          workspaceId ?? undefined,
          undefined, // role
          undefined, // status
          inputValue || undefined,
          1,
          SEARCH_PAGE_SIZE,
          undefined, // ids
          excludeIds && excludeIds.length > 0 ? excludeIds : undefined
        );
        if (id !== reqIdRef.current) return;
        setOptions(result.items ?? []);
      } catch {
        if (id !== reqIdRef.current) return;
        setOptions([]);
      } finally {
        if (id === reqIdRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, open, workspaceId, excludeKey]);

  const hasSelection = multiple
    ? Array.isArray(value) && value.length > 0
    : value != null;

  return (
    <Autocomplete<Login, boolean, false, false>
      multiple={multiple}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value as any}
      onChange={(_e, newValue) => onChange(newValue as any)}
      inputValue={inputValue}
      onInputChange={(_e, val) => setInputValue(val)}
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      getOptionLabel={displayName}
      isOptionEqualToValue={(opt, val) => opt.id === (val as Login)?.id}
      noOptionsText={inputValue ? 'No users match' : 'Type to search'}
      renderTags={(items, getTagProps) =>
        items.map((option, index) => (
          <Chip
            label={displayName(option)}
            size="small"
            {...getTagProps({ index })}
            key={option.id}
          />
        ))
      }
      sx={{
        minWidth: DEFAULT_MIN_WIDTH,
        '& .MuiAutocomplete-clearIndicator': {
          visibility: hasSelection ? 'visible' : 'hidden',
        },
        ...sx,
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
